import { Card, CardContent } from '@/components/ui/card'
import { BarChart3 } from 'lucide-react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import type { VoicePipelineSessionTrendItem } from '@/lib/api/reporting.client'
import { Heading } from '@/components/ui/typography'
import { ChartLoadingState } from '@/components/reporting/ChartComponents'
import { getPipelineChartColors } from '@/lib/constants/chart-colors'
import { useTheme } from '@/contexts/theme.context'

interface TokenTooltipProps {
  active?: boolean
  payload?: Array<{
    name: string
    value: number
    color: string
    payload?: { sessionLabel: string; turnCount: number }
  }>
}

function TokenTooltip({ active, payload }: TokenTooltipProps) {
  if (!active || !payload?.length) return null

  const data = payload[0]?.payload

  return (
    <div className="bg-white dark:bg-background border border-gray-200 dark:border-border rounded-lg shadow-xl p-4 backdrop-blur-sm">
      <p className="text-sm font-semibold text-gray-900 dark:text-foreground mb-1 border-b border-gray-200 dark:border-border pb-2">
        {data?.sessionLabel ?? 'Session'}
      </p>
      <p className="text-xs text-gray-500 dark:text-muted-foreground mb-3">
        {data?.turnCount ?? 0} turns
      </p>
      <div className="space-y-2">
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-sm text-gray-600 dark:text-muted-foreground">
                {entry.name}
              </span>
            </div>
            <span className="text-sm font-bold" style={{ color: entry.color }}>
              {Math.round(entry.value).toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

interface TokenTrendChartProps {
  data: VoicePipelineSessionTrendItem[]
  totalSessionCount?: number
  isLoading: boolean
}

export function TokenTrendChart({
  data,
  totalSessionCount,
  isLoading,
}: TokenTrendChartProps) {
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const colors = getPipelineChartColors(isDark)
  const gridColor = isDark ? '#2a2a2a' : '#f0f0f0'
  const axisTickColor = isDark ? '#9ca3af' : '#6b7280'
  const axisLabelColor = isDark ? '#6b7280' : '#9ca3af'
  const isDownsampled =
    totalSessionCount != null && totalSessionCount > data.length
  const chartData = data.map((d, i) => ({
    index: i + 1,
    sessionLabel: new Date(d.createdAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }),
    turnCount: d.turnCount,
    avgPromptTokens: Math.round(d.avgPromptTokens),
    maxPromptTokens: Math.round(d.maxPromptTokens),
  }))

  const hasData = chartData.length > 0

  // Dynamic Y-axis: pad 20% above max, round down min to nearest 1k
  const allValues = chartData.flatMap(d => [
    d.avgPromptTokens,
    d.maxPromptTokens,
  ])
  const minVal = Math.min(...(allValues.length ? allValues : [0]))
  const maxVal = Math.max(...(allValues.length ? allValues : [0]))
  const yMin = Math.max(0, Math.floor(minVal / 1000) * 1000 - 1000)
  const yMax = Math.ceil((maxVal * 1.2) / 1000) * 1000

  return (
    <Card className="bg-white dark:bg-background shadow-sm border border-gray-200 dark:border-border hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <Heading variant="section-title" className="mb-1">
          Prompt Token Trend
        </Heading>
        <p className="text-sm text-gray-500 dark:text-muted-foreground mb-4">
          Average prompt tokens per turn — rising trend signals context bloat
          {isDownsampled && (
            <span className="text-xs text-gray-400 dark:text-muted-foreground ml-2">
              — showing {data.length} of {totalSessionCount.toLocaleString()}{' '}
              sessions
            </span>
          )}
        </p>

        {isLoading ? (
          <ChartLoadingState />
        ) : !hasData ? (
          <div className="flex flex-col items-center justify-center h-[300px] text-center px-4">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-muted flex items-center justify-center mb-4">
              <BarChart3 className="h-8 w-8 text-gray-400 dark:text-muted-foreground" />
            </div>
            <p className="text-gray-500 dark:text-muted-foreground font-medium mb-1">
              No data available
            </p>
            <p className="text-sm text-gray-400 dark:text-muted-foreground">
              No pipeline metrics for the selected period
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-end gap-4 mb-2">
              {[
                {
                  label: 'Avg Prompt Tokens',
                  color: colors.avgTokens,
                },
                {
                  label: 'Max Prompt Tokens',
                  color: colors.maxTokens,
                },
              ].map(({ label, color }) => (
                <div key={label} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-xs font-medium text-gray-700 dark:text-muted-foreground">
                    {label}
                  </span>
                </div>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart
                data={chartData}
                margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={gridColor}
                  vertical={false}
                />
                <XAxis
                  dataKey="index"
                  stroke={axisTickColor}
                  style={{ fontSize: '10px', fontWeight: 500 }}
                  tickLine={false}
                  axisLine={false}
                  label={{
                    value: 'Session #',
                    position: 'insideBottomRight',
                    offset: -5,
                    style: { fontSize: '10px', fill: axisLabelColor },
                  }}
                />
                <YAxis
                  stroke={axisTickColor}
                  style={{ fontSize: '11px', fontWeight: 500 }}
                  tickLine={false}
                  axisLine={false}
                  domain={[yMin, yMax]}
                  tickFormatter={v => Number(v).toLocaleString()}
                />
                <Tooltip content={<TokenTooltip />} />
                <Line
                  type="monotone"
                  dataKey="avgPromptTokens"
                  stroke={colors.avgTokens}
                  strokeWidth={3}
                  dot={{ fill: colors.avgTokens, r: 2 }}
                  activeDot={{ r: 4 }}
                  name="Avg Prompt Tokens"
                  isAnimationActive={false}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <Line
                  type="monotone"
                  dataKey="maxPromptTokens"
                  stroke={colors.maxTokens}
                  strokeWidth={2}
                  dot={{ fill: colors.maxTokens, r: 2 }}
                  activeDot={{ r: 4 }}
                  name="Max Prompt Tokens"
                  isAnimationActive={false}
                  strokeDasharray="5 5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
      </CardContent>
    </Card>
  )
}
